import type {
  FetchProductsArgs,
  CreateProductArgs,
  UpdateProductArgs,
  ArchiveProductArgs,
  FetchSalesArgs,
  FetchProductPurchasesArgs,
  CreateProductPurchaseArgs,
  VoidProductPurchaseArgs,
  ApiProductPurchase,
  VoidSaleArgs,
  CreateSaleArgs,
  ApiSaleListItem,
  ApiProduct,
  ApiSale,
} from './types/desk.types';

import { destroy, patch, post, get } from './axios';

export const fetchProducts = async ({
  organizationId,
  ...query
}: FetchProductsArgs) => {
  const response = await get<{ data: ApiProduct[]; total: number }>(
    `/v2/organizations/${organizationId}/desk/products`,
    {
      query: {
        search: query.search,
        offset: query.offset?.toString(),
        limit: query.limit?.toString(),
        orderBy: query.orderBy,
        orderDirection: query.orderDirection,
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const createProduct = async ({
  organizationId,
  ...body
}: CreateProductArgs) => {
  const response = await post<{ data: ApiProduct }>(
    `/v2/organizations/${organizationId}/desk/products`,
    body,
  );
  return response?.data ?? null;
};

export const updateProduct = async ({
  organizationId,
  productId,
  ...body
}: UpdateProductArgs) => {
  const response = await patch<{ data: { id: string } }>(
    `/v2/organizations/${organizationId}/desk/products/${productId}`,
    body,
  );
  return response?.data ?? null;
};

export const archiveProduct = ({
  organizationId,
  productId,
}: ArchiveProductArgs) =>
  destroy(`/v2/organizations/${organizationId}/desk/products/${productId}`);

export const createSale = async ({ organizationId, dto }: CreateSaleArgs) => {
  const response = await post<{ data: ApiSale[] }>(
    `/v2/organizations/${organizationId}/desk/sales`,
    dto,
  );
  return response?.data ?? [];
};

export const fetchSales = async ({
  organizationId,
  ...query
}: FetchSalesArgs) => {
  const response = await get<{ data: ApiSaleListItem[]; total: number }>(
    `/v2/organizations/${organizationId}/desk/sales`,
    {
      query: {
        from: query.from,
        to: query.to,
        orderDirection: query.orderDirection,
        offset: query.offset?.toString(),
        limit: query.limit?.toString(),
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const voidSale = async ({
  organizationId,
  saleId,
  reason,
}: VoidSaleArgs) => {
  const response = await post<{ data: ApiSale }>(
    `/v2/organizations/${organizationId}/desk/sales/${saleId}/void`,
    { reason },
  );
  return response?.data ?? null;
};

// 2.11 — the desk's stock-in

export const fetchProductPurchases = async ({
  organizationId,
  ...query
}: FetchProductPurchasesArgs) => {
  const response = await get<{ data: ApiProductPurchase[]; total: number }>(
    `/v2/organizations/${organizationId}/desk/purchases`,
    {
      query: {
        includeVoided: query.includeVoided ? 'true' : undefined,
        limit: query.limit?.toString(),
        offset: query.offset?.toString(),
        productId: query.productId,
      },
    },
  );
  return { data: response?.data ?? [], total: response?.total ?? 0 };
};

export const createProductPurchase = async ({
  organizationId,
  ...body
}: CreateProductPurchaseArgs) => {
  const response = await post<{ data: ApiProductPurchase }>(
    `/v2/organizations/${organizationId}/desk/purchases`,
    body,
  );
  return response?.data ?? null;
};

export const voidProductPurchase = async ({
  organizationId,
  purchaseId,
  reason,
}: VoidProductPurchaseArgs) => {
  const response = await post<{ data: ApiProductPurchase }>(
    `/v2/organizations/${organizationId}/desk/purchases/${purchaseId}/void`,
    { reason },
  );
  return response?.data ?? null;
};
